/**
 * Market Data Store
 * Manages instruments, prices, and market data
 */

import { create } from 'zustand'
import type { Instrument, Price, Candle, Timeframe, WatchlistItem } from '../types'
import { apiClient } from '../services/api'

interface MarketState {
  instruments: Instrument[]
  prices: Map<string, Price>
  watchlist: WatchlistItem[]
  isLoading: boolean
  error: string | null
  lastInstrumentsFetch: number | null // Cache timestamp

  // Actions
  loadInstruments: (force?: boolean) => Promise<void>
  loadWatchlist: () => Promise<void>
  addToWatchlist: (instrument: string, notes?: string) => Promise<void>
  removeFromWatchlist: (instrument: string) => Promise<void>
  updatePrice: (instrument: string, price: Price) => void
  getHistoricalData: (instrument: string, timeframe: Timeframe, limit?: number) => Promise<Candle[]>
  clearError: () => void
}

const INSTRUMENTS_CACHE_TTL = 5 * 60 * 1000 // 5 minutes cache

export const useMarketStore = create<MarketState>((set, get) => ({
  instruments: [],
  prices: new Map(),
  watchlist: [],
  isLoading: false,
  error: null,
  lastInstrumentsFetch: null,

  loadInstruments: async (force = false) => {
    const state = get()
    const now = Date.now()
    
    // Use cache if available and not expired (unless forced)
    if (!force && state.instruments.length > 0 && state.lastInstrumentsFetch) {
      const age = now - state.lastInstrumentsFetch
      if (age < INSTRUMENTS_CACHE_TTL) {
        console.log('Using cached instruments, age:', Math.round(age / 1000), 'seconds')
        return
      }
    }

    set({ isLoading: true, error: null })
    try {
      const instruments = await apiClient.getInstruments()
      set({ 
        instruments, 
        isLoading: false,
        lastInstrumentsFetch: now
      })
    } catch (error: any) {
      set({
        error: error.response?.data?.error || 'Failed to load instruments',
        isLoading: false,
      })
    }
  },

  loadWatchlist: async () => {
    try {
      const watchlist = await apiClient.getWatchlist()
      set({ watchlist })
    } catch (error: any) {
      set({ error: error.response?.data?.error || 'Failed to load watchlist' })
    }
  },

  addToWatchlist: async (instrument: string, notes?: string) => {
    try {
      await apiClient.addToWatchlist(instrument, notes)
      await get().loadWatchlist()
    } catch (error: any) {
      set({ error: error.response?.data?.error || 'Failed to add to watchlist' })
      throw error
    }
  },

  removeFromWatchlist: async (instrument: string) => {
    try {
      await apiClient.removeFromWatchlist(instrument)
      await get().loadWatchlist()
    } catch (error: any) {
      set({ error: error.response?.data?.error || 'Failed to remove from watchlist' })
      throw error
    }
  },

  updatePrice: (instrument: string, price: Price) => {
    const prices = new Map(get().prices)
    prices.set(instrument, price)
    set({ prices })
  },

  getHistoricalData: async (instrument: string, timeframe: Timeframe, limit: number = 100) => {
    try {
      return await apiClient.getHistoricalData(instrument, timeframe, limit)
    } catch (error: any) {
      set({ error: error.response?.data?.error || 'Failed to load historical data' })
      throw error
    }
  },

  clearError: () => set({ error: null }),
}))
